import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ScrollText, Mail, Phone, MapPin } from "lucide-react";

const LAST_UPDATED = "24 October 2026";

export default function Terms() {
  useEffect(() => { document.title = "Terms of Service — ZeroLabs Digitals"; }, []);

  return (
    <div data-testid="page-terms" className="pt-24 pb-24">
      <section className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#22D3EE]/10 text-[#22D3EE] flex items-center justify-center">
            <ScrollText size={22}/>
          </div>
          <div>
            <div className="eyebrow">Legal</div>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-1">Terms of Service</h1>
          </div>
        </div>
        <p className="mt-3 text-sm text-[#8C859E] font-mono">Last updated: {LAST_UPDATED}</p>

        <div className="mt-10 space-y-8 text-[#D6D2E1] leading-relaxed">
          <Section title="1. Acceptance of Terms">
            <p>
              By accessing or using <span className="text-[#22D3EE]">zerolabsdigi.in</span>, engaging with our AI assistant "Zero",
              submitting a contact or booking form, or using any product operated by
              <strong className="text-white"> ZeroLabs Digitals</strong> ("we", "us"), you agree to be bound by these Terms of Service
              ("Terms") and our <Link to="/privacy-policy" className="text-[#22D3EE] hover:underline">Privacy Policy</Link>.
              If you do not agree, please do not use the Services.
            </p>
          </Section>

          <Section title="2. Services">
            <p>ZeroLabs Digitals provides:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Custom software development services — web, mobile, AI automation and plant engineering.</li>
              <li>SaaS products including <strong className="text-white">School Management System</strong> and <strong className="text-white">MediQ</strong>.</li>
              <li>Consultations, technical support, and advisory services.</li>
              <li>The website itself, including AI chat and AI voice-agent interactions.</li>
            </ul>
            <p className="mt-3">We may update, add, or discontinue features at any time.</p>
          </Section>

          <Section title="3. Eligibility">
            <p>
              You must be at least 18 years of age and legally capable of forming a binding contract in India (or your
              jurisdiction of residence) to use the Services. By using the Services you represent that you meet these criteria.
            </p>
          </Section>

          <Section title="4. Accounts">
            <ul className="list-disc pl-5 space-y-2">
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You are responsible for all activities under your account.</li>
              <li>You agree to notify us immediately of any unauthorised use.</li>
              <li>We may suspend or terminate accounts that violate these Terms or applicable law.</li>
            </ul>
          </Section>

          <Section title="5. Acceptable use">
            <p>You agree <strong className="text-white">not</strong> to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Use the Services for anything illegal, harmful, defamatory or infringing on rights of others.</li>
              <li>Attempt to reverse-engineer, decompile, or extract source code from our products.</li>
              <li>Introduce viruses, malware, or attempt to disrupt or overload our infrastructure.</li>
              <li>Scrape, cache, or reproduce any part of the site without written permission.</li>
              <li>Use the AI chatbot or voice agent to solicit personal data of third parties, generate illegal content,
                or automate abusive behaviour.</li>
              <li>Impersonate any person or entity, or misrepresent your affiliation with any organisation.</li>
            </ul>
          </Section>

          <Section title="6. AI-generated content">
            <p>
              Our AI assistant Zero provides responses generated using large language models. While we work hard to keep
              answers accurate and helpful, AI output may contain <strong className="text-white">errors, omissions or
              outdated information</strong>. You should independently verify any commercial, legal, medical or engineering
              information before acting on it. We are not liable for reliance on AI-generated content beyond what is
              explicitly stated in a signed engagement letter.
            </p>
          </Section>

          <Section title="7. Consultations & bookings">
            <ul className="list-disc pl-5 space-y-2">
              <li>Free 30-minute consultations are subject to availability and do not constitute a binding engagement.</li>
              <li>Written project scopes, timelines and prices are provided separately by email or contract.</li>
              <li>You may reschedule or cancel a booking free of charge by replying to the confirmation email at least 4 hours before the scheduled time.</li>
            </ul>
          </Section>

          <Section title="8. Payments">
            <p>
              For paid engagements, invoices are issued in Indian Rupees (INR) unless otherwise agreed. Payment terms and
              taxes (including GST where applicable) will be specified in your engagement letter or product subscription
              agreement. Late payments may incur interest at 1.5% per month.
            </p>
          </Section>

          <Section title="9. Intellectual property">
            <p>
              All content on <span className="text-[#22D3EE]">zerolabsdigi.in</span>, including text, graphics, logos, source code,
              product designs, videos and the ZeroLabs Digitals name and marks, is the property of ZeroLabs Digitals or its
              licensors and is protected by copyright, trademark and other applicable laws. You may not use any part of it
              without our prior written permission, except as expressly permitted by these Terms.
            </p>
            <p className="mt-3">
              For custom development engagements, IP ownership is transferred to the client upon full payment and as
              specifically defined in the engagement letter.
            </p>
          </Section>

          <Section title="10. Third-party links and services">
            <p>
              Our Services may link to or interoperate with third-party websites, APIs and services. We are not responsible
              for their content, availability, security or privacy practices. Your use of such services is at your own risk
              and subject to their terms.
            </p>
          </Section>

          <Section title="11. Warranty disclaimer">
            <p>
              The Services are provided <strong className="text-white">"as is" and "as available"</strong>, without warranties
              of any kind, whether express or implied, including merchantability, fitness for a particular purpose,
              non-infringement, or uninterrupted operation. We do not guarantee that the Services will be free of errors,
              or that defects will be corrected on any specific timeline.
            </p>
          </Section>

          <Section title="12. Limitation of liability">
            <p>
              To the maximum extent permitted by law, ZeroLabs Digitals and its officers, employees, and partners will not
              be liable for any indirect, incidental, consequential, punitive or exemplary damages arising from your use of
              the Services. Our total aggregate liability for any claim arising out of these Terms shall not exceed the
              amount you paid to us in the twelve (12) months preceding the claim, or <strong className="text-white">₹10,000</strong>,
              whichever is greater.
            </p>
          </Section>

          <Section title="13. Indemnity">
            <p>
              You agree to indemnify and hold harmless ZeroLabs Digitals from any claims, damages or expenses (including
              reasonable legal fees) arising from your breach of these Terms or misuse of the Services.
            </p>
          </Section>

          <Section title="14. Termination">
            <p>
              We may suspend or terminate your access to the Services at any time, with or without notice, for any conduct
              that we believe violates these Terms or is harmful to other users or to us. You may stop using the Services at
              any time. Sections 6, 9, 11–13, and 15 survive termination.
            </p>
          </Section>

          <Section title="15. Governing law & jurisdiction">
            <p>
              These Terms are governed by the laws of India. Any dispute arising out of or in connection with these Terms
              shall be subject to the exclusive jurisdiction of the courts at <strong className="text-white">Davangere, Karnataka</strong>.
            </p>
          </Section>

          <Section title="16. Changes to these Terms">
            <p>
              We may modify these Terms from time to time. When we do, we will post the updated version on this page and
              update the "Last updated" date at the top. Continued use of the Services after changes take effect constitutes
              acceptance of the revised Terms.
            </p>
          </Section>

          <Section title="17. Contact us">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 mt-2 space-y-2">
              <p className="text-white font-semibold">ZeroLabs Digitals</p>
              <p className="text-sm flex items-center gap-2"><Mail size={14} className="text-[#22D3EE]"/> <a className="text-[#22D3EE] hover:underline" href="mailto:info@zerolabsdigi.in">info@zerolabsdigi.in</a></p>
              <p className="text-sm flex items-center gap-2"><Phone size={14} className="text-[#22D3EE]"/> <a className="text-[#22D3EE] hover:underline" href="tel:+918971452224">+91 8971452224</a></p>
              <p className="text-sm flex items-start gap-2"><MapPin size={14} className="mt-1 text-[#22D3EE] shrink-0"/><span>4172, 3rd Floor, 17th Main, 5th Cross, MCC B Block, Davangere — 577004, Karnataka, India.</span></p>
            </div>
          </Section>
        </div>

        <div className="mt-14 text-sm text-[#8C859E]">
          See also our <Link to="/privacy-policy" className="text-[#22D3EE] hover:underline">Privacy Policy</Link>.
        </div>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl md:text-2xl font-semibold text-white tracking-tight mb-3">{title}</h2>
      <div className="text-[#D6D2E1] text-[15px]">{children}</div>
    </section>
  );
}
